import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  RxGrid,
  cellId,
  committedAxis,
  committedDioptre,
  emptyGrid,
  type GridState,
} from '@lensmira/ui'
import {
  RX_TYPES,
  toNumber,
  validateAxis,
  validateCylinder,
  validateSphere,
  type Finding,
  type PasteResult,
  type RxType,
} from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { sentence } from '../sentence.js'
import { rxTypeLabel } from '../status-labels.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Prescription capture. PRD §6.3 `RXW-001`, `RXW-002`; `DESIGN-TOKENS.md` §9.
 *
 * Release gate 1 is measured here: a two-eye single-vision prescription captured keyboard-only in
 * under twenty seconds.
 *
 * The screen owns three things the grid deliberately does not: where the value goes (the write
 * queue, never a fetch), what warns (Appendix I, via `domain`), and what the customer is called.
 */

export interface RxCaptureProps {
  readonly customerId: string
  readonly customerName: string
  /**
   * Values to start from, each already decided by a person (`AI-001`): Mira's accepted fields.
   * A prefill and not a save, so the dispenser still reads the grid and still presses Save, and the
   * write goes through this screen's own validation and idempotency key like any other.
   */
  readonly initialGrid?: GridState
  /** Called once the server has the prescription, with its id when the answer carried one. */
  readonly onSaved?: (prescriptionId?: string) => void
}

/**
 * `RX-05`: "Out-of-range values take a warn tint with a left edge and an inline reason. **They
 * warn; they never block.**"
 *
 * So only the warnings are surfaced here. Appendix I's blocking rules exist and belong to order
 * entry and dispense, where a lens product exists to be out of range of, and where release gate 3
 * measures them. Running a blocking validator on this screen is how `RX-05` would quietly stop
 * being true, so the block findings are dropped on purpose rather than by omission.
 */
function warningsFor(state: GridState): Record<string, string> {
  const out: Record<string, string> = {}
  const warn = (row: 'od' | 'os', column: string, findings: readonly Finding[]): void => {
    const first = findings.find((f) => f.severity === 'warn')
    if (first) out[cellId(row, column as never)] = first.message
  }

  for (const row of ['od', 'os'] as const) {
    const sphere = committedDioptre(state[row].sphere)
    const cylinder = committedDioptre(state[row].cylinder)
    const axis = committedAxis(state[row].axis)

    if (sphere !== undefined) warn(row, 'sphere', validateSphere(sphere))
    if (cylinder !== undefined) warn(row, 'cylinder', validateCylinder(cylinder))
    if (cylinder !== undefined) warn(row, 'axis', validateAxis(axis, cylinder))
  }
  return out
}

export function RxCapture({
  customerId,
  customerName,
  initialGrid,
  onSaved,
}: RxCaptureProps): JSX.Element {
  const [state, setState] = useState<GridState>(() => initialGrid ?? emptyGrid())
  /**
   * §5.2 makes the prescription type a header field, and the grid does not carry it: the grid is
   * six columns of numbers and the type is a judgement the dispenser makes.
   *
   * It was hardcoded to 'spectacle' when this screen was first written, which is not an Appendix E
   * `rx_type` at all, so every save this screen made was refused by the column's check constraint.
   * Nothing in the suite caught it because nothing in the suite ran the real API. `distance` is the
   * default because it is the commonest prescription at a counter, and it is a visible choice
   * rather than a guess derived from whether an add happens to be filled in.
   */
  const [type, setType] = useState<RxType>('distance')
  const [paste, setPaste] = useState<PasteResult | null>(null)
  /**
   * The id of the write this screen queued, so the status line can report **that write's** fate
   * rather than the fact that a save was attempted.
   *
   * The first version of this screen kept a boolean `saved`, set when `enqueue` resolved. Running
   * the built app against no API produced the line "Online, 1 need attention. Prescription saved."
   * in a single breath: the write had been refused with a 404 and the screen said it had saved.
   *
   * `OFF-005` `MUST`: "Staff must never be uncertain whether a sale is safe." A boolean that means
   * "we tried" cannot express that, so it was replaced with the write's real status.
   */
  const [writeId, setWriteId] = useState<string | null>(null)
  const { enqueue, snapshot } = useWriteQueue()
  const { t } = useTranslation()

  useEffect(() => {
    // RXW-001 is timed from the screen appearing, so focus starts in the first cell rather than
    // costing a click or a tab. KB-02: "Every frequent action reachable without a mouse."
    document.getElementById(cellId('od', 'sphere'))?.focus()
  }, [])

  const warnings = warningsFor(state)

  const save = useCallback(async () => {
    // OFF-001a: this is an enqueue, not a fetch. Nothing on this screen can reach the network.
    // ARC-006's idempotency key is minted by the queue, so a double-tapped save is one prescription.
    const eye = (row: 'od' | 'os'): Record<string, number | undefined> => {
      const sphere = committedDioptre(state[row].sphere)
      const cylinder = committedDioptre(state[row].cylinder)
      const add = committedDioptre(state[row].add)
      return {
        sphere: sphere === undefined ? undefined : toNumber(sphere),
        cylinder: cylinder === undefined ? undefined : toNumber(cylinder),
        axis: committedAxis(state[row].axis),
        add: add === undefined ? undefined : toNumber(add),
      }
    }

    const write = (await enqueue({
      method: 'POST',
      path: '/prescriptions',
      label: t('rxCapture.heading', { name: customerName }),
      body: { customerId, type, od: eye('od'), os: eye('os') },
    })) as { id: string }
    setWriteId(write.id)
  }, [customerId, customerName, enqueue, state, t, type])

  /**
   * The parent hears about this when the server has the prescription, not when the queue does.
   *
   * `App.tsx` answers `onSaved` by clearing the customer, which unmounts this screen. Called on the
   * line after `enqueue` resolved, that happened before the POST had been sent, so the `writeId` and
   * `statusLine` machinery this file carries three paragraphs of comment about could never report
   * `sent` or `blocked`: a refusal such as `RX_SPHERE_REQUIRED` arrived after the screen and the
   * typed prescription were both gone, leaving the shop with a `blocked` write in the queue and no
   * idea which prescription it belonged to.
   *
   * The prescription's id goes with it, because an order raised straight afterwards needs it and it
   * exists nowhere else on the client. `RX-001`: a job is made to a prescription, and an order that
   * carries none cannot freeze one into `JOB-002`'s snapshot later.
   */
  useWriteSettled(snapshot, writeId, (write) => {
    const body = write.response
    const id =
      typeof body === 'object' && body !== null && typeof (body as { id?: unknown }).id === 'string'
        ? (body as { id: string }).id
        : undefined
    onSaved?.(id)
  })

  return (
    <section className="lm-screen" aria-labelledby="rx-capture-heading">
      <h1 id="rx-capture-heading" className="lm-screen__heading">
        {t('rxCapture.heading', { name: customerName })}
      </h1>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('rxCapture.typeLabel')}</span>
        <select
          className="lm-input"
          value={type}
          onChange={(e) => setType(e.currentTarget.value as RxType)}
        >
          {RX_TYPES.filter((rxType) => rxType !== 'contact_lens').map((rxType) => (
            <option key={rxType} value={rxType}>
              {rxTypeLabel(t, rxType)}
            </option>
          ))}
        </select>
      </label>

      <RxGrid value={state} onChange={setState} warnings={warnings} onPaste={setPaste} />

      {paste && (paste.rejections.length > 0 || paste.assumptions.length > 0) ? (
        // RXW-002 asks for parsing, and a paste that silently drops half its input is worse than
        // one that refuses out loud: the dispenser believes it worked and never looks again.
        <div className="lm-paste-report" role="status">
          {paste.rejections.length > 0 ? (
            <p className="lm-paste-report__line">
              {/* The rejected cells are dioptric text, and a dioptric sign is the figure that
                  actually reorders: measured in Chromium against real Nastaliq, a bare `-3.25`
                  inside a right-to-left run renders `3.25-`, which a shopkeeper reads as 3.25.
                  `AI-001`'s own wording is the cost, "a wrong sphere value is a remake", so this
                  screen is the one to isolate first. `sentence()` keeps it one `t()` call
                  (Appendix S) rather than splitting the message in two. */}
              {sentence(t, 'rxCapture.pasteRejected', {
                cells: <Ltr>{paste.rejections.map((r) => r.text).join(', ')}</Ltr>,
              })}
            </p>
          ) : null}
          {paste.assumptions.map((a) => (
            <p key={`${a.text}:${a.assumption}`} className="lm-paste-report__line">
              {/* `a.text` is what was pasted, so it carries the sign too. The note beside it is
                  English from `packages/domain` and has no key here, which `en.ts` records. */}
              <Ltr>{a.text}</Ltr>: {a.assumption}
            </p>
          ))}
        </div>
      ) : null}

      <div className="lm-screen__actions">
        <button
          type="button"
          className="lm-button lm-button--primary"
          onClick={() => {
            void save()
          }}
        >
          {t('rxCapture.save')}
        </button>
        <p className="lm-hint">{t('rxCapture.keyboardHint')}</p>
      </div>

      {/* OFF-005: "Visible connection state and pending-sync count. Staff must never be uncertain
          whether a sale is safe." The same rule applies to a prescription, and the wording below is
          one sentence about one write rather than two clauses that can contradict each other. */}
      <p className="lm-queue-state" role="status">
        {statusLine(t, snapshot, writeId, t('queue.subject.prescription'))}
      </p>
    </section>
  )
}
